import { createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { LeaseTemplateRepository } from '~/repositories/leaseTemplateRepository';
import { LeasesRenewalsRepository } from '~/repositories/leasesRenewalsRepository';
import { LeaseSettingsRepository } from '~/repositories/settings/leaseSettingsRepository';
import { createTriggerResource } from '~/utils/resource';
import type { LeasesRenewalsFilter } from '~/repositories/leasesRenewalsRepository';

const repo = new LeasesRenewalsRepository();
const leaseTemplateRepo = new LeaseTemplateRepository();
const leaseSettingsRepo = new LeaseSettingsRepository();

type Filter = LeasesRenewalsFilter & { leaseId: string };

export type NewLeaseRenewalOfferListItem = MagicDoor.Api.LeaseRenewalOfferListItemDto & {
  chartOfAccounts: MagicDoor.Api.ChartOfAccountDto[];
  leases: MagicDoor.Api.LeaseDto[];
};

export type GroupedLeaseTemplatesItem = {
  templates: MagicDoor.Api.LeaseTemplateDto[];
  id: string;
  name: string;
  isDefault: boolean;
};

export const [LeasesRenewalsProvider, useLeasesRenewals] = createMagicDoorContext('LeasesRenewals', () => {
  const [loading, setLoading] = createSignal<boolean>(false);
  const [groupedLeaseTemplates, setGroupedLeaseTemplates] = createSignal<GroupedLeaseTemplatesItem[]>([]);

  const [leasesRenewals, getLeasesRenewals, leasesRenewalsActions] = createTriggerResource(async (params: Filter) => {
    const response = await repo.getRenewalOffers(params.leaseId, {
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 100,
      statuses: params.statuses,
    });
    return {
      ...response,
      items: response.items.map((item) => ({
        ...item,
        chartOfAccounts: response.chartOfAccounts,
        leases: response.leases,
      })) as NewLeaseRenewalOfferListItem[],
    };
  });

  const applyOffer = async (leaseId: string, offerId: string): Promise<void> => {
    const response = await repo.applyOffer(leaseId, offerId);
    leasesRenewalsActions.mutate(
      (prev) =>
        ({
          ...prev,
          items:
            prev?.items?.map((item) =>
              item.id === offerId
                ? {
                    ...item,
                    status: response.status,
                  }
                : item
            ) ?? [],
        }) as any
    );
  };

  const createLeaseRenewalOffer = async (leaseId: string, data: MagicDoor.Api.CreateLeaseRenewalOfferDto, apply = false) => {
    try {
      setLoading(true);
      if (apply) {
        await repo.createAndApplyLeaseRenewalOffer(leaseId, data);
      } else {
        await repo.createLeaseRenewalOffer(leaseId, data);
      }
    } finally {
      setLoading(false);
    }
  };

  const getGroupedLeaseTemplates = async () => {
    const categories = await leaseTemplateRepo.getLeaseTemplateCategories();
    const leaseTemplates = await leaseTemplateRepo.getLeaseTemplates();
    const allowedLeaseTemplateTypes = ['lease', 'leaseRenewal', 'information'];
    const newLeaseTemplates = leaseTemplates?.filter((item) => allowedLeaseTemplateTypes.includes(item.type));
    const groupedLeaseTemplates = categories
      ?.map((category) => ({
        ...category,
        templates: newLeaseTemplates?.filter((template) => template.leaseTemplateCategoryId === category.id),
      }))
      .filter((item) => item.templates?.length);

    setGroupedLeaseTemplates(groupedLeaseTemplates);
  };

  const deleteOffer = async (leaseId: string, offerId: string) => {
    await repo.deleteOffer(leaseId, offerId);
    leasesRenewalsActions.mutate(
      (prev) =>
        ({
          ...prev,
          items:
            prev?.items?.map((item) =>
              item.id === offerId
                ? {
                    ...item,
                    status: 'retracted',
                  }
                : item
            ) ?? [],
        }) as any
    );
  };

  const sendSigning = async (leaseId: string, offerId: string) => {
    await repo.sendSigning(leaseId, offerId);
    leasesRenewalsActions.mutate(
      (prev) =>
        ({
          ...prev,
          items:
            prev?.items?.map((item) =>
              item.id === offerId
                ? {
                    ...item,
                    status: 'signing',
                  }
                : item
            ) ?? [],
        }) as any
    );
  };

  const isLeaseTemplateSet = async (leaseId: string) => {
    const res = await leaseSettingsRepo.getLeaseSettingsByLevel('lease', leaseId);
    return !!res.leaseTemplates?.length;
  };

  return {
    leasesRenewals,
    getLeasesRenewals,
    leasesRenewalsActions,
    applyOffer,
    createLeaseRenewalOffer,
    loading,
    getGroupedLeaseTemplates,
    groupedLeaseTemplates,
    deleteOffer,
    sendSigning,
    isLeaseTemplateSet,
  };
});
